import { getEventUiGroup } from '../trigger-event-options';

const TRIGGER_GROUPS = {
  ADMIN: 'admin',
  DRIVE: 'drive',
  LOGIN: 'login',
  CALENDAR: 'calendar',
};

const TRIGGER_EVENTS = {
  CREATE_USER: 'CREATE_USER',
  ROLE_PERMISSION_CHANGE: 'ROLE_PERMISSION_CHANGE',
  DELETE_USER: 'DELETE_USER',
  CREATE_GROUP: 'CREATE_GROUP',
  CHANGE_PASSWORD: 'CHANGE_PASSWORD',
  UPDATE_USER: 'UPDATE',
  ROLE_MEMBER_UPDATE: 'ROLE_MEMBER_UPDATE',
  CREATE_CALENDAR_EVENT: 'create_event',
  CHANGE_CALENDAR_EVENT: 'change_event',
  SHARED_CALENDAR: 'change_calendar_acls',
  DOCUMENT_SHARED: 'change_user_access',
};

const actionAvailabilityRules: {
  [key: string]: (triggerGroup: string, triggerEvent: string) => boolean;
} = {
  default: (triggerGroup: string, _triggerEvent: string) =>
    [TRIGGER_GROUPS.ADMIN, TRIGGER_GROUPS.DRIVE, TRIGGER_GROUPS.LOGIN].includes(
      triggerGroup
    ),
  suspendUser: (triggerGroup: string, triggerEvent: string) =>
    triggerEvent === TRIGGER_EVENTS.DELETE_USER ||
    triggerGroup === TRIGGER_GROUPS.DRIVE,
  resetPassword: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.DRIVE ||
    [
      TRIGGER_EVENTS.CREATE_USER,
      TRIGGER_EVENTS.ROLE_PERMISSION_CHANGE,
      TRIGGER_EVENTS.CREATE_GROUP,
      TRIGGER_EVENTS.CHANGE_PASSWORD,
      TRIGGER_EVENTS.ROLE_MEMBER_UPDATE,
    ].includes(triggerEvent),
  changeCalendarTimezone: (_triggerGroup: string, triggerEvent: string) =>
    [
      TRIGGER_EVENTS.CREATE_USER,
      TRIGGER_EVENTS.ROLE_PERMISSION_CHANGE,
      TRIGGER_EVENTS.CREATE_GROUP,
      TRIGGER_EVENTS.ROLE_MEMBER_UPDATE,
    ].includes(triggerEvent),
  signOut: (_triggerGroup: string, triggerEvent: string) =>
    [TRIGGER_EVENTS.DELETE_USER, TRIGGER_EVENTS.CHANGE_PASSWORD].includes(
      triggerEvent
    ),
  changePassword: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.ADMIN &&
    triggerEvent !== TRIGGER_EVENTS.UPDATE_USER,
  runPolicy: (triggerGroup: string, triggerEvent: string) =>
    (triggerGroup === TRIGGER_GROUPS.ADMIN &&
      triggerEvent !== TRIGGER_EVENTS.UPDATE_USER) ||
    [TRIGGER_GROUPS.LOGIN].includes(triggerGroup),
  addToGroup: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.DRIVE ||
    (triggerGroup === TRIGGER_GROUPS.ADMIN &&
      [
        TRIGGER_EVENTS.CREATE_USER,
        TRIGGER_EVENTS.ROLE_PERMISSION_CHANGE,
        TRIGGER_EVENTS.CREATE_GROUP,
        TRIGGER_EVENTS.UPDATE_USER,
        TRIGGER_EVENTS.ROLE_MEMBER_UPDATE,
      ].includes(triggerEvent)),
  removeFromGroup: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.DRIVE ||
    (triggerGroup === TRIGGER_GROUPS.ADMIN &&
      triggerEvent === TRIGGER_EVENTS.UPDATE_USER),
  limitOccurrences: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.CALENDAR &&
    [
      TRIGGER_EVENTS.CREATE_CALENDAR_EVENT,
      TRIGGER_EVENTS.CHANGE_CALENDAR_EVENT,
      TRIGGER_EVENTS.SHARED_CALENDAR,
    ].includes(triggerEvent),
  applyGroupTemplate: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.ADMIN &&
    triggerEvent === TRIGGER_EVENTS.CREATE_GROUP,
  sendEmailTemplate: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.ADMIN &&
    [
      TRIGGER_EVENTS.CREATE_USER,
      TRIGGER_EVENTS.ROLE_PERMISSION_CHANGE,
      TRIGGER_EVENTS.ROLE_MEMBER_UPDATE,
    ].includes(triggerEvent),
  deleteGroup: (triggerGroup: string, _triggerEvent: string) =>
    [
      TRIGGER_GROUPS.ADMIN,
      TRIGGER_GROUPS.LOGIN,
      TRIGGER_GROUPS.CALENDAR,
    ].includes(triggerGroup),
  removeShare: (triggerGroup: string, triggerEvent: string) =>
    triggerGroup === TRIGGER_GROUPS.DRIVE &&
    triggerEvent === TRIGGER_EVENTS.DOCUMENT_SHARED,
};

const groupEventActions = ['sendEmail', 'sendChatMessage', 'deleteGroup'];

export const checkActionAvailability = (
  actionValue: string,
  triggerGroup: string,
  triggerEvent: string
): boolean => {
  const isGroupEvent = getEventUiGroup(triggerEvent) === 'groupEvents';
  if (isGroupEvent) {
    return groupEventActions.includes(actionValue);
  }
  const rule =
    actionAvailabilityRules[actionValue] || actionAvailabilityRules.default;
  return rule(triggerGroup, triggerEvent);
};
